"use client";

import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, setFetcherToken, setRefreshFn } from "@/lib/api";
import {
  authEndImpersonation,
  authLogin,
  authLogout,
  authPasswordResetComplete,
  authPasswordResetRequest,
  authRefresh,
  authRegister,
  fetchMe,
  type AuthUser,
  type RefreshResponse,
} from "@/features/auth/api";
import { identifyChokh, resetChokh } from "@/lib/chokh";
import { clearQuizCreatorDraftsForUser } from "@/features/quiz/creator-draft-storage";
import { queryKeys } from "@/lib/query-keys";
import { readSessionHint, writeSessionHint } from "@/lib/session-hint";
import { forgetSavedView, isSavedRead, restoreSavedView, startSavingView } from "@/lib/saved-view";

type AuthContextValue = {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True when the session is an admin impersonation (“login as user”) token. */
  isImpersonating: boolean;
  /**
   * True while the first session check keeps failing for a reason that proves
   * nothing about the session (the API is down, a gateway answered 502, a
   * deploy is restarting it). The page is still loading; this only lets it say
   * so instead of showing a skeleton with no end.
   */
  sessionCheckFailing: boolean;
  /** Runs the session check again now, instead of waiting for the next retry. */
  retrySessionCheck: () => void;
  /** Returns the current in-memory access token — pass to authenticated API calls. */
  getToken: () => string | null;
  /** `roomCode` (`EX-VEN01`): the hall's code, when an issued login's contest asks for it. */
  login: (email: string, password: string, roomCode?: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    phone?: string,
    institution?: string,
  ) => Promise<{ userId: string }>;
  /** Sends a reset link to the address. Never signals whether that address has an account. */
  requestPasswordReset: (email: string) => Promise<void>;
  completePasswordReset: (token: string, newPassword: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Ends impersonation on the server, clears the web session, and redirects to the admin panel URL from the API. */
  endImpersonation: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/**
 * The same context for the surfaces that render outside the provider on
 * purpose. The embeddable Playground (`PG-EMBED01`) is the one that exists: it
 * runs the anonymous path inside a publisher's page and has no session to read,
 * so a component shared with the signed-in shell asks with this and treats
 * `null` as "no account", never as an error.
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

/** Decode JWT exp without verifying the signature. */
function getTokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Detect impersonation JWTs — must not use refresh cookie rotation while active. */
function isImpersonationAccessToken(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1] ?? "")) as {
      imp?: boolean;
      typ?: string;
    };
    return payload.imp === true && payload.typ === "impersonation";
  } catch {
    return false;
  }
}

/**
 * Best-effort: call the logout endpoint to clear the HttpOnly `rt` cookie.
 * This breaks the middleware redirect loop caused by a stale cookie that
 * passes the edge cookie-presence check but fails backend token validation.
 */
function clearRefreshCookie(): void {
  authLogout(null).catch(() => {});
}

/** Only an explicit auth refusal proves that the refresh session is no longer valid. */
function isTerminalSessionError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/**
 * How long the first session check waits before asking again after a failure
 * that proves nothing about the session. A deploy restarts the API in seconds,
 * so the first retries are quick; after that it settles at fifteen seconds, the
 * single interval it used before 2026-09-24.
 */
export const BOOTSTRAP_RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 15_000] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  /**
   * Access token lives in a ref — mutations don't trigger re-renders.
   * React state (tokenReady, isBootstrapping) drives query enablement and loading UI.
   */
  const tokenRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tokenReady, setTokenReady] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [sessionCheckFailing, setSessionCheckFailing] = useState(false);
  const bootstrapNowRef = useRef<(() => void) | null>(null);
  const queryClient = useQueryClient();

  const clearInvalidSession = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    tokenRef.current = null;
    setFetcherToken(null);
    setTokenReady(true);
    queryClient.clear();
    forgetSavedView();
    clearRefreshCookie();
    writeSessionHint("none");
  }, [queryClient]);

  /**
   * A refresh answers with the account since `AD-DSH02`, so the profile is
   * set from it instead of asked for again; an API without the flag leaves it
   * out and `/v1/me` is read as before.
   */
  const adoptRefreshedAccount = useCallback(
    async (data: RefreshResponse) => {
      if (data.user) queryClient.setQueryData(queryKeys.auth.me, data.user);
      else await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    },
    [queryClient],
  );

  /**
   * Schedule a proactive token refresh 30 s before expiry.
   * Uses Math.max(0, delay) so a near-expired or already-expired token
   * triggers an immediate refresh instead of silently being skipped.
   */
  const scheduleRefresh = useCallback(
    (token: string) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (isImpersonationAccessToken(token)) return;
      const expiry = getTokenExpiryMs(token);
      if (!expiry) return;
      const delay = Math.max(0, expiry - Date.now() - 30_000);
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const data = await authRefresh();
          tokenRef.current = data.accessToken;
          setFetcherToken(data.accessToken);
          scheduleRefresh(data.accessToken);
          await adoptRefreshedAccount(data);
        } catch (e) {
          if (isTerminalSessionError(e)) {
            clearInvalidSession();
            return;
          }

          // A network/5xx failure does not invalidate the cookie. Keep the
          // current user visible and retry after a short backoff.
          const currentToken = tokenRef.current;
          if (currentToken) {
            refreshTimerRef.current = setTimeout(() => scheduleRefresh(currentToken), 15_000);
          }
        }
      }, delay);
    },
    [adoptRefreshedAccount, clearInvalidSession]
  );

  /**
   * Register a module-level refresh function so fetcher() can silently
   * refresh expired tokens and retry failed requests on 401 without
   * needing React context. Deregistered on unmount.
   */
  useEffect(() => {
    const refreshFn = async (): Promise<string | null> => {
      if (isImpersonationAccessToken(tokenRef.current)) {
        return null;
      }
      try {
        const data = await authRefresh();
        tokenRef.current = data.accessToken;
        setFetcherToken(data.accessToken);
        scheduleRefresh(data.accessToken);
        await adoptRefreshedAccount(data);
        return data.accessToken;
      } catch (e) {
        if (isTerminalSessionError(e)) clearInvalidSession();
        return null;
      }
    };
    setRefreshFn(refreshFn);
    return () => setRefreshFn(null);
  }, [adoptRefreshedAccount, clearInvalidSession, scheduleRefresh]);

  /** On mount: silently attempt refresh so returning users stay logged in. */
  useEffect(() => {
    let mounted = true;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let failures = 0;

    // One check at a time: a retry asked for while one is running (the
    // reconnect button, the `online` event) waits for that one's answer.
    async function bootstrap() {
      if (inFlight) return;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      inFlight = true;
      try {
        await attempt();
      } finally {
        inFlight = false;
      }
    }

    async function attempt() {
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const impTok = params.get("impersonationToken");
        if (impTok?.trim()) {
          const cleaned = impTok.trim();
          tokenRef.current = cleaned;
          setFetcherToken(cleaned);
          setTokenReady(true);
          setIsBootstrapping(false);
          const url = new URL(window.location.href);
          url.searchParams.delete("impersonationToken");
          const nextSearch = url.searchParams.toString();
          window.history.replaceState({}, "", `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`);
          return;
        }
      }
      // A browser that knows it holds no refresh cookie (its last refresh was
      // rejected, or the user logged out) skips the round trip; until
      // 2026-09-03 every anonymous page view sent a refresh and then a logout.
      // "session" and an absent hint both ask the API, as before.
      const hint = readSessionHint();
      if (hint === "none") {
        tokenRef.current = null;
        setFetcherToken(null);
        setTokenReady(true);
        setIsBootstrapping(false);
        return;
      }
      try {
        const data = await authRefresh();
        if (mounted) {
          tokenRef.current = data.accessToken;
          setFetcherToken(data.accessToken);
          scheduleRefresh(data.accessToken);
          if (data.user) {
            // The account came with the token: no second round trip for it, and,
            // now that the API has said who this is, a return visit opens on the
            // answers it saw last time (`lib/saved-view.ts`) while they refresh.
            queryClient.setQueryData(queryKeys.auth.me, data.user);
            if (data.user.accountKind !== "contest_temp") restoreSavedView(queryClient, data.user._id);
          }
          setTokenReady(true);
          setIsBootstrapping(false);
          setSessionCheckFailing(false);
          writeSessionHint("session");
        }
      } catch (e) {
        if (!mounted) return;

        if (isTerminalSessionError(e)) {
          // The backend has definitively rejected the refresh cookie. When this
          // browser had a session, clear the cookie so middleware cannot bounce
          // between login and dashboard forever; a browser that never had one
          // has nothing to clear, and that logout call was pure waste.
          if (hint === "session") clearRefreshCookie();
          writeSessionHint("none");
          forgetSavedView();
          tokenRef.current = null;
          setFetcherToken(null);
          setTokenReady(true); // no valid session, allow unauthenticated state
          setIsBootstrapping(false);
          setSessionCheckFailing(false);
          return;
        }

        // A browser that knows it is offline cannot answer "is the session
        // still valid" until it is back (`PG-PWA01`): settle as signed out so
        // the page stops waiting on a skeleton, and ask once more on `online`.
        // The refresh cookie is untouched, so the session comes back with the
        // network.
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          tokenRef.current = null;
          setFetcherToken(null);
          setTokenReady(true);
          setIsBootstrapping(false);
          window.addEventListener("online", () => void bootstrap(), { once: true });
          return;
        }

        // A temporary network/API failure does not prove the session ended.
        // Keep the auth boundary loading and retry instead of rendering the
        // signed-out UI while the refresh cookie may still be valid, and say
        // so: until 2026-09-24 this was a bare skeleton for as long as it lasted.
        failures += 1;
        setSessionCheckFailing(true);
        const delay =
          BOOTSTRAP_RETRY_DELAYS_MS[Math.min(failures, BOOTSTRAP_RETRY_DELAYS_MS.length) - 1];
        retryTimer = setTimeout(() => void bootstrap(), delay);
      }
    }

    bootstrapNowRef.current = () => void bootstrap();
    bootstrap();
    return () => {
      mounted = false;
      bootstrapNowRef.current = null;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [queryClient, scheduleRefresh]);

  const retrySessionCheck = useCallback(() => {
    bootstrapNowRef.current?.();
  }, []);

  /**
   * Re-check token validity when the tab becomes visible again.
   * setTimeout-based refresh timers don't fire while a tab is backgrounded
   * or the device is asleep, so the access token can expire undetected.
   */
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !tokenRef.current) return;
      if (isImpersonationAccessToken(tokenRef.current)) return;
      const expiry = getTokenExpiryMs(tokenRef.current);
      // Refresh proactively if token expires within 60 s
      if (!expiry || expiry - Date.now() < 60_000) {
        authRefresh()
          .then((data) => {
            tokenRef.current = data.accessToken;
            setFetcherToken(data.accessToken);
            scheduleRefresh(data.accessToken);
            void adoptRefreshedAccount(data);
          })
          .catch((e) => {
            if (isTerminalSessionError(e)) {
              clearInvalidSession();
              return;
            }

            // Preserve the current session during a transient outage. The
            // normal expiry scheduler will try again with a short backoff.
            const currentToken = tokenRef.current;
            if (currentToken) scheduleRefresh(currentToken);
          });
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [adoptRefreshedAccount, clearInvalidSession, scheduleRefresh]);

  /** Cleanup refresh timer on unmount. */
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  /**
   * TanStack Query owns /me data once the access token is established.
   * staleTime: 5 min — avoids redundant network calls on navigation.
   */
  const { data: user, isPending: isMePending } = useQuery<AuthUser | null>({
    queryKey: queryKeys.auth.me,
    queryFn: async () => {
      const token = tokenRef.current;
      if (!token) return null;
      try {
        const data = await fetchMe(token);
        return data.user ?? null;
      } catch (e) {
        // An auth refusal is a signed-out state. Network, 5xx, or malformed
        // payload failures must stay errors so TanStack Query retains the last
        // valid profile instead of replacing it with null.
        if (typeof console !== "undefined" && console.error) {
          console.error("[AuthProvider] fetchMe failed", e);
        }
        if (isTerminalSessionError(e)) return null;
        throw e;
      }
    },
    enabled: tokenReady && !!tokenRef.current,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (isTerminalSessionError(error)) return false;
      return failureCount < 2;
    },
  });

  // isPending (status === 'pending') is true from the moment the query becomes enabled
  // until the first successful fetch — no transient false-loading window unlike isLoading.
  const isLoading =
    isBootstrapping || (tokenReady && !!tokenRef.current && isMePending);

  const isAuthenticated = !isLoading && !!user;
  const isImpersonating = !!user?.impersonation;

  /**
   * Keep the saved view (`lib/saved-view.ts`) for a full account signed in as
   * itself. An impersonation is an administrator looking, and a contest
   * temporary account never reaches the dashboard, so neither writes one.
   */
  const savingUserId =
    user && !user.impersonation && user.accountKind !== "contest_temp" ? user._id : null;
  const savedAccountRef = useRef<string | null>(null);
  useEffect(() => {
    if (!savingUserId) return;
    // A sign-in as somebody else in another tab reaches this one through a
    // refresh: what the cache still holds for the previous account must not
    // be saved as this one's.
    if (savedAccountRef.current && savedAccountRef.current !== savingUserId) {
      queryClient.removeQueries({ predicate: (query) => isSavedRead(query.queryKey) });
    }
    savedAccountRef.current = savingUserId;
    return startSavingView(queryClient, savingUserId);
  }, [queryClient, savingUserId]);

  /**
   * Tell Chokh who this visitor is, once per resolved account (`AN-INT01`).
   *
   * `analyticsSignature` is the site's proof of the id, issued server-side;
   * `ps_web` runs with `allowUnsignedIdentify` off, so until `AN-INT02` puts
   * it on the auth payload this identify is refused and the visitor's events
   * are collected anonymously, which is the order the ticket asks for.
   * Impersonation is deliberately not identified: those page views belong to
   * the admin doing the looking, not to the student being looked at.
   */
  const identifiedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || user.impersonation) return;
    if (identifiedRef.current === user._id) return;
    identifiedRef.current = user._id;
    identifyChokh(user._id, user.analyticsSignature);
  }, [user]);

  const getToken = useCallback(() => tokenRef.current, []);

  const login = useCallback(
    async (email: string, password: string, roomCode?: string) => {
      const data = await authLogin(email, password, roomCode);
      tokenRef.current = data.accessToken;
      setFetcherToken(data.accessToken);
      scheduleRefresh(data.accessToken);
      queryClient.setQueryData(queryKeys.auth.me, data.user);
      setIsBootstrapping(false);
      setTokenReady(true);
      writeSessionHint("session");
    },
    [queryClient, scheduleRefresh]
  );

  const register = useCallback(
    async (name: string, email: string, password: string, phone?: string, institution?: string) => {
      const data = await authRegister(name, email, password, phone, institution);
      tokenRef.current = data.accessToken;
      setFetcherToken(data.accessToken);
      scheduleRefresh(data.accessToken);
      queryClient.setQueryData(queryKeys.auth.me, data.user);
      setIsBootstrapping(false);
      setTokenReady(true);
      writeSessionHint("session");
      return { userId: data.user._id };
    },
    [queryClient, scheduleRefresh]
  );

  const requestPasswordReset = useCallback(async (email: string) => {
    await authPasswordResetRequest(email);
  }, []);

  const completePasswordReset = useCallback(
    async (token: string, newPassword: string, confirmPassword: string) => {
      const data = await authPasswordResetComplete(token, newPassword, confirmPassword);
      tokenRef.current = data.accessToken;
      setFetcherToken(data.accessToken);
      scheduleRefresh(data.accessToken);
      queryClient.setQueryData(queryKeys.auth.me, data.user);
      setIsBootstrapping(false);
      setTokenReady(true);
      writeSessionHint("session");
    },
    [queryClient, scheduleRefresh]
  );

  const logout = useCallback(async () => {
    clearQuizCreatorDraftsForUser(user?._id);
    // Forget the account and, in persistent mode, the stored visitor id, so the
    // next person on a shared browser is a new visitor and not this one.
    identifiedRef.current = null;
    resetChokh();
    try {
      await authLogout(tokenRef.current);
    } catch {
      // logout is best-effort
    }
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    tokenRef.current = null;
    setFetcherToken(null);
    setTokenReady(false);
    queryClient.clear();
    forgetSavedView();
    writeSessionHint("none");
  }, [queryClient, user?._id]);

  const endImpersonation = useCallback(async () => {
    const t = tokenRef.current;
    if (!t) return;
    const { adminPanelUrl } = await authEndImpersonation(t);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    tokenRef.current = null;
    setFetcherToken(null);
    setTokenReady(false);
    queryClient.clear();
    window.location.assign(adminPanelUrl);
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        isAuthenticated,
        isImpersonating,
        sessionCheckFailing: isBootstrapping && sessionCheckFailing,
        retrySessionCheck,
        getToken,
        login,
        register,
        requestPasswordReset,
        completePasswordReset,
        logout,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
